/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import clsx from 'clsx';

import { DisplayDate } from '@tock/time';
import { Typography } from 'omakase';
import { core } from '@tock/coretypes';
import NextReleaseDateTime from 'components/business/landing-page/NextReleaseDateTime';

interface Props {
  businessName: string;
  businessTicketsAvailableUntil?: string;
  hasOfferings?: boolean;
  nextRelease?: core.IBookingRelease;
  offeringsDisabled?: boolean;
  soldOut?: boolean;
  styleAsCard?: boolean;
  className?: string;
}

interface AvailabilityMessage {
  type: 'AVAILABLE' | 'UNAVAILABLE';
  offeringsMessageHeading: any;
  offeringsMessage: any;
}

const NEXT_RELEASE_MESSAGE = 'New reservations will be released on';

const OfferingsStatusMessage: React.FunctionComponent<Props> = ({
  businessName,
  businessTicketsAvailableUntil,
  className,
  hasOfferings,
  nextRelease,
  offeringsDisabled,
  soldOut,
  styleAsCard,
}) => {
  let availabilityMessage: AvailabilityMessage;
  if (hasOfferings) {
    const offeringsMessageHeading = (
      <>
        {soldOut ? (
          'All reservations sold out'
        ) : (
          <>
            Now booking through{' '}
            <DisplayDate
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              dateValue={businessTicketsAvailableUntil!}
              displayMonthAs="long"
              showWeekday={true}
            />
          </>
        )}
      </>
    );

    const offeringsMessage = nextRelease ? (
      <>
        {NEXT_RELEASE_MESSAGE}{' '}
        <NextReleaseDateTime
          dateTimeEpochMillis={
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            nextRelease.activeOnEpochMillis!
          }
        />
      </>
    ) : soldOut ? (
      <>All reservations for {businessName} have been sold. Please check again soon.</>
    ) : (
      ''
    );
    availabilityMessage = {
      type: soldOut ? 'UNAVAILABLE' : 'AVAILABLE',
      offeringsMessageHeading,
      offeringsMessage,
    };
  } else {
    availabilityMessage = {
      type: 'UNAVAILABLE',
      offeringsMessageHeading: 'Reservations are unavailable',
      offeringsMessage:
        !offeringsDisabled && nextRelease ? (
          <>
            {NEXT_RELEASE_MESSAGE}{' '}
            <NextReleaseDateTime
              dateTimeEpochMillis={
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                nextRelease.activeOnEpochMillis!
              }
            />
          </>
        ) : (
          `${businessName} is not currently accepting reservations on Tock. Please check again soon.`
        ),
    };
  }

  if (!nextRelease && availabilityMessage.type === 'AVAILABLE') {
    return null;
  } else {
    return (
      <div
        className={clsx('OfferingsStatusMessage', className, {
          'OfferingsStatusMessage--card': styleAsCard,
        })}
      >
        <Typography
          variant="body02Strong"
          component="h3"
          className="OfferingsStatusMessage-heading"
        >
          {availabilityMessage.offeringsMessageHeading}.{' '}
        </Typography>
        {availabilityMessage.offeringsMessage && (
          <Typography className="OfferingsStatusMessage-message" variant="body02" component="p">
            {availabilityMessage.offeringsMessage}
          </Typography>
        )}
      </div>
    );
  }
};

export default OfferingsStatusMessage;
